import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiGet, apiPut } from '@/services/api-client';
import { logActivity } from '@/lib/activity-logger';
import { useAuthStore } from './auth-store';

export type PrepGoal = '7d' | '30d' | '90d';
export type TimeLevel = '7d' | '30d' | '90d';

interface FamilyMember {
  id: string;
  name: string;
  role: 'adult' | 'child' | 'pet';
  dailyCalories?: number; // Kaloribehov per dag (endast för adult/child)
}

interface ServerSettings {
  vmaRegion: string;
  vmaPostalCode: string;
  vmaCountyCodes: string[];
  vmaNotifications: boolean;
  prepGoal: PrepGoal;
  hidePrepTestCard?: boolean;
  householdSize: number;
  userDailyCalories?: number;
  familyMembers: FamilyMember[];
  notifications: {
    vmaAlerts: boolean;
    expirationWarnings: boolean;
    yearWheelReminders: boolean;
    waterReminders: boolean;
    familyChat: boolean;
    groupMembershipRequests: boolean;
    groupMembershipDecisions: boolean;
    groupInvitations: boolean;
    groupActivity: boolean;
    groupThreadReplies: boolean;
  };
}

interface SettingsState {
  // Beredskapsmål
  prepGoal: PrepGoal;
  setPrepGoal: (goal: PrepGoal) => void;
  
  // Hushåll
  householdSize: number;
  setHouseholdSize: (size: number) => void;
  userDailyCalories: number;
  setUserDailyCalories: (calories: number) => void;
  familyMembers: FamilyMember[];
  addFamilyMember: (member: Omit<FamilyMember, 'id'>) => void;
  updateFamilyMember: (id: string, updates: Partial<Omit<FamilyMember, 'id'>>) => void;
  removeFamilyMember: (id: string) => void;
  
  // VMA-region
  vmaRegion: string;
  vmaPostalCode: string;
  setVmaRegion: (region: string, postalCode?: string) => void;
  
  // Notiser
  notifications: {
    vmaAlerts: boolean;
    expirationWarnings: boolean;
    yearWheelReminders: boolean;
    waterReminders: boolean;
    familyChat: boolean;
    groupMembershipRequests: boolean;
    groupMembershipDecisions: boolean;
    groupInvitations: boolean;
    groupActivity: boolean;
    groupThreadReplies: boolean;
  };
  setNotification: (key: keyof SettingsState['notifications'], value: boolean) => void;
  
  // Hjälpfunktioner
  getTimeLevelsForGoal: () => TimeLevel[];
  getMaxTimeLevel: () => TimeLevel;
  getPeopleCount: () => number;  // Antal personer (vuxna + barn, exkl husdjur)
  getAdultsCount: () => number;  // Antal vuxna (inkl kontohållaren)
  getChildrenCount: () => number;  // Antal barn
  
  // Synk
  isSyncing: boolean;
  lastSyncAt: string | null;
  _isRehydrating: boolean; // Added _isRehydrating flag
  fetchFromServer: () => Promise<void>;
  syncToServer: () => Promise<void>;
  // UI preferences
  hidePrepTestCard?: boolean;
  setHidePrepTestCard: (val: boolean) => void;
  
  // Hydration
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

// Mappa beredskapsmål till tidsnivåer
const goalToLevels: Record<PrepGoal, TimeLevel[]> = {
  '7d': ['7d'],
  '30d': ['7d', '30d'],
  '90d': ['7d', '30d', '90d'],
};

const goalToMaxLevel: Record<PrepGoal, TimeLevel> = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
};

// Migration från gamla prepGoal-värden
const migrateOldPrepGoal = (oldGoal: string): PrepGoal => {
  const migrations: Record<string, PrepGoal> = {
    '72h': '7d',
    '1week': '7d',
    '2weeks': '30d',
    '1month': '90d',
  };
  return migrations[oldGoal] || (oldGoal as PrepGoal) || '7d';
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // Beredskapsmål - default 7 dagar
      prepGoal: '7d',
      setPrepGoal: (goal) => {
        set({ prepGoal: goal });
        if (!get()._isRehydrating) get().syncToServer();
      },
      
      // Hushåll
      householdSize: 1,
      setHouseholdSize: (size) => {
        set({ householdSize: size });
        if (!get()._isRehydrating) get().syncToServer();
      },
      userDailyCalories: 2000,
      setUserDailyCalories: (calories) => {
        set({ userDailyCalories: calories });
        if (!get()._isRehydrating) get().syncToServer();
      },
      familyMembers: [],
      addFamilyMember: (member) => {
        // Sätt default kalorivärden baserat på roll
        const defaultCalories = member.role === 'adult' ? 2000 : member.role === 'child' ? 1500 : undefined;
        
        set((state) => ({
          familyMembers: [
            ...state.familyMembers,
            { 
              ...member, 
              id: Date.now().toString(),
              dailyCalories: member.dailyCalories ?? defaultCalories
            }
          ],
          householdSize: state.householdSize + 1,
        }));
        if (!get()._isRehydrating) get().syncToServer();
      },
      updateFamilyMember: (id, updates) => {
        set((state) => ({
          familyMembers: state.familyMembers.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          ),
        }));
        if (!get()._isRehydrating) get().syncToServer();
      },
      removeFamilyMember: (id) => {
        set((state) => ({
          familyMembers: state.familyMembers.filter((m) => m.id !== id),
          householdSize: Math.max(1, state.householdSize - 1),
        }));
        if (!get()._isRehydrating) get().syncToServer();
      },
      
      // VMA-region
      vmaRegion: '',
      vmaPostalCode: '',
      setVmaRegion: (region, postalCode) => {
        set({ 
          vmaRegion: region, 
          vmaPostalCode: postalCode || '' 
        });
        if (!get()._isRehydrating) get().syncToServer();
      },
      
      // Notiser
      notifications: {
        vmaAlerts: true,
        expirationWarnings: true,
        yearWheelReminders: true,
        waterReminders: true,
        familyChat: true,
        groupMembershipRequests: true,
        groupMembershipDecisions: true,
        groupInvitations: true,
        groupActivity: true,
        groupThreadReplies: true,
      },
      setNotification: (key, value) => {
        set((state) => ({
          notifications: { ...state.notifications, [key]: value },
        }));
        if (!get()._isRehydrating) get().syncToServer();
      },
      
      // Hjälpfunktioner
      getTimeLevelsForGoal: () => {
        const { prepGoal } = get();
        // Fallback om prepGoal är ett gammalt värde
        return goalToLevels[prepGoal] || goalToLevels['7d'];
      },
      getMaxTimeLevel: () => {
        const { prepGoal } = get();
        // Fallback om prepGoal är ett gammalt värde
        return goalToMaxLevel[prepGoal] || goalToMaxLevel['7d'];
      },
      getPeopleCount: () => {
        const { familyMembers } = get();
        // Räkna vuxna och barn (exkluderar husdjur), plus kontohållaren (1 vuxen)
        const familyPeople = familyMembers.filter(m => m.role === 'adult' || m.role === 'child').length;
        return 1 + familyPeople; // +1 för kontohållaren
      },
      getAdultsCount: () => {
        const { familyMembers } = get();
        // Räkna vuxna i familjen plus kontohållaren (1 vuxen)
        const adults = familyMembers.filter(m => m.role === 'adult').length;
        return 1 + adults; // +1 för kontohållaren
      },
      getChildrenCount: () => {
        const { familyMembers } = get();
        return familyMembers.filter(m => m.role === 'child').length;
      },
      
      // Synk
      isSyncing: false,
      lastSyncAt: null,
      _isRehydrating: false,
      
      fetchFromServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;

        // Guard against local setters triggering sync while we apply
        // authoritative server-side values.
        try { set({ _isRehydrating: true, isSyncing: true }); } catch { set({ isSyncing: true }); }

        try {
          const { getEffectiveFamilyOwnerId } = await import('./family-store');
          const ownerId = getEffectiveFamilyOwnerId();
          const familyId = ownerId || useAuthStore.getState().user?.familyId;
          const qs = familyId ? `?familyId=${encodeURIComponent(familyId)}` : '';
          const result = await apiGet<ServerSettings>(`/settings${qs}`);

          if (result.success && result.data) {
            const data = result.data as ServerSettings & { updatedAt?: string };
            const serverUpdatedAt = data.updatedAt as string | undefined;

            // Server is authoritative for family membership and prepGoal.
            // Always apply these server values to avoid a device with stale
            // local state overwriting the owner's settings.
            set({
              vmaRegion: data.vmaRegion || '',
              vmaPostalCode: data.vmaPostalCode || '',
              prepGoal: data.prepGoal || '7d',
              hidePrepTestCard: data.hidePrepTestCard ?? false,
              householdSize: data.householdSize || 1,
              userDailyCalories: data.userDailyCalories || 2000,
              familyMembers: data.familyMembers || [],
              notifications: {
                vmaAlerts: data.notifications?.vmaAlerts ?? true,
                expirationWarnings: data.notifications?.expirationWarnings ?? true,
                yearWheelReminders: data.notifications?.yearWheelReminders ?? true,
                waterReminders: data.notifications?.waterReminders ?? true,
                familyChat: data.notifications?.familyChat ?? true,
                groupMembershipRequests: data.notifications?.groupMembershipRequests ?? true,
                groupMembershipDecisions: data.notifications?.groupMembershipDecisions ?? true,
                groupInvitations: data.notifications?.groupInvitations ?? true,
                groupActivity: data.notifications?.groupActivity ?? true,
                groupThreadReplies: data.notifications?.groupThreadReplies ?? true,
              },
              isSyncing: false,
              lastSyncAt: serverUpdatedAt || new Date().toISOString(),
            });
          } else {
            set({ isSyncing: false });
          }
        } catch (error) {
          console.warn('Failed to fetch settings:', error);
          set({ isSyncing: false });
        } finally {
          try { set({ _isRehydrating: false }); } catch { void 0; }
        }
      },

      syncToServer: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) {
          console.warn('[Settings] Sync skipped: User not authenticated');
          return;
        }

        const { vmaRegion, vmaPostalCode, prepGoal, householdSize, userDailyCalories, familyMembers, notifications } = get();

        try {
          const result = await apiPut('/settings', {
            vmaRegion,
            vmaPostalCode,
            prepGoal,
            hidePrepTestCard: get().hidePrepTestCard,
            householdSize,
            userDailyCalories,
            familyMembers,
            notifications,
          });
          
          if (result.success) {
            logActivity({ action: 'UPDATE_SETTINGS', category: 'settings', description: 'Settings updated' });
            // Update lastSyncAt so we know local state is in sync with server
            try {
              set({ lastSyncAt: new Date().toISOString() });
            } catch {
              // ignore
            }
          } else {
            console.error('[Settings] Sync failed:', result.error);
          }
        } catch (error) {
          console.error('[Settings] Failed to sync settings:', error);
        }
      },

      hidePrepTestCard: false,
      setHidePrepTestCard: (val: boolean) => {
        set({ hidePrepTestCard: val });
        if (!get()._isRehydrating) get().syncToServer();
      },
      
      // Hydration
      _hasHydrated: false,
      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'beredskapshubben-settings',
      onRehydrateStorage: () => (state) => {
        // Migrera gamla prepGoal-värden
        if (state) {
          // Indicate we are rehydrating so syncs triggered by setters are suppressed
          try { state._isRehydrating = true; } catch { void 0; }

          const currentGoal = state.prepGoal;
          const validGoals: PrepGoal[] = ['7d', '30d', '90d'];
          if (!validGoals.includes(currentGoal)) {
            state.setPrepGoal(migrateOldPrepGoal(currentGoal));
          }
          state.setHasHydrated(true);

          // Clear rehydration flag after a short delay to allow any derived setters to finish
          try {
            setTimeout(() => {
              try { useSettingsStore.getState()._isRehydrating = false; } catch { void 0; }
            }, 50);
          } catch { void 0; }

          // Prenumerera på ändringar i auth-store för att automatiskt
          // återställa prepGoal till '7d' om användaren tappar Premium.
          try {
            // Dynamic import to avoid circular dependencies at module init
            import('./auth-store')
              .then((authMod) => {
                // Subscribe to subscriptionTier changes
                // Track previous value because listener receives only the new value in some typings
                let prevTier = authMod.useAuthStore.getState().user?.subscriptionTier;
                authMod.useAuthStore.subscribe((s: unknown) => {
                  try {
                    const newTier = (s as unknown as { user?: { subscriptionTier?: 'free' | 'premium' } })?.user?.subscriptionTier;
                    const oldTier = prevTier;
                    prevTier = newTier;
                    if (oldTier === 'premium' && newTier !== 'premium') {
                      // If user downgraded from premium, ensure prepGoal is reverted
                      (async () => {
                        try {
                          const mod = await import('./settings-store');
                          const settings = mod.useSettingsStore.getState();
                          if (settings.prepGoal !== '7d') {
                            settings.setPrepGoal('7d');
                          }
                        } catch (e) {
                          // best-effort
                          console.warn('[Settings] Failed to apply prepGoal reset after downgrade', e);
                        }
                      })();
                    }
                  } catch (e) {
                    // swallow errors to avoid breaking hydration
                    console.warn('[Settings] Failed to handle auth subscription change', e);
                  }
                });
              })
              .catch((err) => {
                // If dynamic import fails, ignore - subscription is best-effort
                console.warn('[Settings] Could not subscribe to auth-store for premium downgrade handling', err);
              });
          } catch (e) {
            // swallow errors to avoid breaking hydration
            console.warn('[Settings] Could not initiate auth-store subscription', e);
          }
        }
      },
    }
  )
);

// Helper hook för att kolla hydration status
export const useSettingsHydrated = () => useSettingsStore((state) => state._hasHydrated);
